import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './GasDistributorClientesNovosConfigModal.css';
import AppIcon from './icons/AppIcon';

/**
 * Modal de configuração específico para a lista "Clientes Novos"
 * Permite configurar:
 * - Instruções personalizadas para o primeiro atendimento
 * - Tags automáticas ao cadastrar cliente
 */
export default function GasDistributorClientesNovosConfigModal({ list, onClose, onSave, projectId }) {
  const [loading, setLoading] = useState(false);
  const [conexaoConfigurada, setConexaoConfigurada] = useState(null);
  
  // Modo de Cadastro
  const [modoCadastro, setModoCadastro] = useState('com_agente'); // 'com_agente' ou 'automatico'
  
  // Instruções para Primeiro Atendimento
  const [instrucoesAtivadas, setInstrucoesAtivadas] = useState(false);
  const [instrucoesPrimeiroAtendimento, setInstrucoesPrimeiroAtendimento] = useState('');
  
  // Tags Automáticas
  const [tagsAtivadas, setTagsAtivadas] = useState(false);
  const [tagsSelecionadas, setTagsSelecionadas] = useState([]);
  const [tagsDisponiveis, setTagsDisponiveis] = useState([]);
  
  // Carregar configurações do projeto
  useEffect(() => {
    const loadProjectConfig = async () => {
      try {
        const effectiveProjectId = projectId || list?.project_id;
        
        if (!effectiveProjectId) {
          console.error('projectId não disponível');
          return;
        }

        // Buscar projeto com settings
        const { data: project, error: projectError } = await supabase
          .from('projects')
          .select('settings')
          .eq('id', effectiveProjectId)
          .maybeSingle();

        if (projectError) {
          console.error('Erro ao buscar projeto:', projectError);
        }

        // Buscar conexão vinculada ao projeto
        const connectionId = project?.settings?.gas_distributor?.connection_id;
        if (connectionId) {
          const { data: connection, error: connError } = await supabase
            .from('connections')
            .select('id, name, number')
            .eq('id', connectionId)
            .maybeSingle();

          if (connError) {
            console.error('Erro ao buscar conexão:', connError);
          } else if (connection) {
            setConexaoConfigurada(connection);
          }
        }

        // Buscar tags configuradas no projeto
        const tagsConfiguradas = project?.settings?.gas_distributor?.tags_configuradas || [];
        setTagsDisponiveis(tagsConfiguradas);
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
      }
    };

    loadProjectConfig();
  }, [projectId, list?.project_id]);

  // Carregar configuração da lista
  useEffect(() => {
    const loadListConfig = async () => {
      if (!list?.id) return;
      
      try {
        const { data: listData, error } = await supabase
          .from('project_lists')
          .select('metadata')
          .eq('id', list.id)
          .maybeSingle();

        if (error) throw error;

        const metadata = listData?.metadata || {};
        
        // Modo de cadastro
        const modoCadastroConfig = metadata.modo_cadastro || {};
        setModoCadastro(modoCadastroConfig.tipo || 'com_agente');
        
        // Instruções para primeiro atendimento
        const primeiroAtendimento = metadata.primeiro_atendimento || {};
        setInstrucoesAtivadas(primeiroAtendimento.enabled || false);
        setInstrucoesPrimeiroAtendimento(primeiroAtendimento.instrucoes || '');
        
        // Tags automáticas
        const tagsAutomaticas = metadata.tags_automaticas || {};
        setTagsAtivadas(tagsAutomaticas.enabled || false);
        setTagsSelecionadas(tagsAutomaticas.tags_selecionadas || []);
      } catch (error) {
        console.error('Erro ao carregar configuração da lista:', error);
      }
    };

    loadListConfig();
  }, [list?.id]);

  const handleSave = async () => {
    try {
      setLoading(true);

      const metadata = list?.metadata || {};
      const updatedMetadata = {
        ...metadata,
        modo_cadastro: {
          tipo: modoCadastro
        },
        primeiro_atendimento: {
          enabled: modoCadastro === 'com_agente' ? instrucoesAtivadas : false,
          instrucoes: instrucoesPrimeiroAtendimento
        },
        tags_automaticas: {
          enabled: modoCadastro === 'com_agente' ? tagsAtivadas : false,
          tags_selecionadas: tagsSelecionadas
        }
      };

      const { error } = await supabase
        .from('project_lists')
        .update({
          metadata: updatedMetadata,
          updated_at: new Date().toISOString()
        })
        .eq('id', list.id);

      if (error) throw error;

      alert('Configurações salvas com sucesso!');
      onSave({ ...list, metadata: updatedMetadata });
      onClose();
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      alert('Erro ao salvar: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="clientes-novos-modal-overlay" onClick={onClose}>
      <div className="clientes-novos-config-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="clientes-novos-modal-header">
          <div className="clientes-novos-modal-title-section">
            <AppIcon name="person_add" />
            <div>
              <h2>Configurar</h2>
              <p className="list-name">{list?.title}</p>
            </div>
          </div>
          <button className="btn-close-modal" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="clientes-novos-modal-content">
          {/* Seção: Informações da Conexão */}
          <div className="clientes-novos-section">
            <div className="section-header">
              <div className="header-left">
                <AppIcon name="link" />
                <h4>Conexão do Projeto</h4>
              </div>
            </div>

            {/* Conexão Configurada */}
            <div className="info-box conexao-info-box">
              <div className="info-header">
                <AppIcon name="phone" className="info-icon" />
                <div className="info-content">
                  <span className="info-label">Conexão Vinculada</span>
                  {conexaoConfigurada ? (
                    <>
                      <span className="info-nome">{conexaoConfigurada.name}</span>
                      <span className="info-numero">{conexaoConfigurada.number}</span>
                    </>
                  ) : (
                    <span className="info-nao-configurada">Nenhuma conexão vinculada</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Seção: Modo de Cadastro */}
          <div className="clientes-novos-section">
            <div className="section-header">
              <div className="header-left">
                <AppIcon name="settings" />
                <h4>Modo de Cadastro</h4>
              </div>
              <div className="tooltip-container">
                <AppIcon name="help" className="tooltip-icon" />
                <div className="tooltip-text">
                  Escolha como os novos clientes serão cadastrados: com agente coletando dados ou automaticamente.
                </div>
              </div>
            </div>

            {/* Radio Buttons para Modo */}
            <div className="modo-cadastro-options">
              <label className="radio-option">
                <input
                  type="radio"
                  name="modoCadastro"
                  value="com_agente"
                  checked={modoCadastro === 'com_agente'}
                  onChange={(e) => setModoCadastro(e.target.value)}
                />
                <div className="radio-content">
                  <span className="radio-title">📋 Cadastro com Agente</span>
                  <span className="radio-description">
                    O agente coleta os dados do cliente antes de cadastrar. Você pode escolher quais campos serão solicitados.
                  </span>
                </div>
              </label>

              <label className="radio-option">
                <input
                  type="radio"
                  name="modoCadastro"
                  value="automatico"
                  checked={modoCadastro === 'automatico'}
                  onChange={(e) => setModoCadastro(e.target.value)}
                />
                <div className="radio-content">
                  <span className="radio-title">⚡ Cadastro Automático (Direto)</span>
                  <span className="radio-description">
                    Cliente é cadastrado automaticamente com nome e telefone do WhatsApp, sem passar pelo agente de cadastro.
                  </span>
                </div>
              </label>
            </div>

            {/* Aviso para modo automático */}
            {modoCadastro === 'automatico' && (
              <div className="modo-automatico-warning">
                <AppIcon name="info" />
                <div>
                  <p><strong>Atenção:</strong> No modo automático, o cliente é cadastrado apenas com nome e telefone do WhatsApp.</p>
                  <p>Dados adicionais (endereço, forma de pagamento) serão coletados pelo agente de atendimento normal quando necessário.</p>
                  <p className="warning-highlight">⚠️ As ferramentas de Tags Automáticas e Instruções de Primeiro Atendimento não estarão disponíveis neste modo.</p>
                </div>
              </div>
            )}
          </div>

          {/* Seção: Instruções para Primeiro Atendimento */}
          {modoCadastro === 'com_agente' && (
            <div className="clientes-novos-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="description" />
                  <h4>Instruções para Primeiro Atendimento</h4>
                </div>
                <div className="tooltip-container">
                  <AppIcon name="help" className="tooltip-icon" />
                  <div className="tooltip-text">
                    Esta configuração será usada somente no primeiro atendimento, quando o cliente ainda não tem cadastro.
                  </div>
                </div>
              </div>

              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={instrucoesAtivadas}
                    onChange={(e) => setInstrucoesAtivadas(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {instrucoesAtivadas 
                      ? '✅ Instruções ativadas' 
                      : '❌ Instruções desativadas'}
                  </span>
                </label>
                <small className="toggle-description">
                  Adicione regras personalizadas que o agente seguirá durante o primeiro contato com o cliente
                </small>
              </div>

              {instrucoesAtivadas && (
                <div className="instrucoes-section">
                  <label className="instrucoes-label">Instruções personalizadas</label>
                  <textarea
                    value={instrucoesPrimeiroAtendimento}
                    onChange={(e) => setInstrucoesPrimeiroAtendimento(e.target.value)}
                    placeholder="Exemplos:&#10;- Verificar se o cliente está na área de cobertura antes de cadastrar&#10;- Perguntar se é pessoa física ou jurídica&#10;- Oferecer desconto para primeira compra&#10;- Perguntar como conheceu a empresa"
                    rows={6}
                    className="instrucoes-textarea"
                  />
                  <small className="instrucoes-hint">
                    Essas instruções serão injetadas no prompt do agente durante o primeiro atendimento
                  </small>
                </div>
              )}
            </div>
          )}

          {/* Seção: Tags Automáticas ao Cadastrar */}
          {modoCadastro === 'com_agente' && (
            <div className="clientes-novos-section">
            <div className="section-header">
              <div className="header-left">
                <AppIcon name="label" />
                <h4>Tags Automáticas ao Cadastrar</h4>
              </div>
              <div className="tooltip-container">
                <AppIcon name="help" className="tooltip-icon" />
                <div className="tooltip-text">
                  Configure regras para adicionar tags automaticamente ao cliente quando ele for cadastrado, baseadas no bairro, cidade ou endereço.
                </div>
              </div>
            </div>

            <div className="tool-enable-section">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={tagsAtivadas}
                  onChange={(e) => setTagsAtivadas(e.target.checked)}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">
                  {tagsAtivadas 
                    ? '✅ Tags automáticas ativadas' 
                    : '❌ Tags automáticas desativadas'}
                </span>
              </label>
              <small className="toggle-description">
                Adicione tags automaticamente ao cliente baseadas em regras de bairro, cidade ou endereço
              </small>
            </div>

            {tagsAtivadas && (
              <div className="tags-section">
                <p className="tags-description">
                  Selecione quais tags configuradas no projeto devem ser analisadas e aplicadas automaticamente quando um cliente for cadastrado. 
                  A IA analisará os dados do cliente e aplicará as tags cujas regras se enquadram.
                </p>

                {tagsDisponiveis.length > 0 ? (
                  <div className="tags-selection-list">
                    {tagsDisponiveis.map((tag) => (
                      <div key={tag.id} className={`tag-selection-item ${!tag.ativa ? 'tag-inactive' : ''}`}>
                        <label className="tag-checkbox-label">
                          <input
                            type="checkbox"
                            checked={tagsSelecionadas.includes(tag.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setTagsSelecionadas([...tagsSelecionadas, tag.id]);
                              } else {
                                setTagsSelecionadas(tagsSelecionadas.filter(id => id !== tag.id));
                              }
                            }}
                            disabled={!tag.ativa}
                          />
                          <div className="tag-selection-info">
                            <div className="tag-selection-header">
                              <AppIcon name={tag.icone} className="tag-selection-icon" style={{ color: tag.cor }} />
                              <div className="tag-selection-details">
                                <span className="tag-selection-name">{tag.nome}</span>
                                {!tag.ativa && <span className="tag-inactive-badge">Inativa</span>}
                              </div>
                            </div>
                            <p className="tag-selection-description">{tag.descricao}</p>
                            <p className="tag-selection-rule">
                              <span className="rule-label-small">Regra:</span> {tag.regra_aplicacao}
                            </p>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-tags-available">
                    <AppIcon name="label_off" />
                    <p>Nenhuma tag configurada no projeto</p>
                    <small>Configure tags no modal de editar projeto para poder selecioná-las aqui</small>
                  </div>
                )}

                {tagsSelecionadas.length > 0 && (
                  <div className="tags-selected-summary">
                    <AppIcon name="check_circle" />
                    <span>{tagsSelecionadas.length} tag{tagsSelecionadas.length > 1 ? 's' : ''} selecionada{tagsSelecionadas.length > 1 ? 's' : ''} para análise automática</span>
                  </div>
                )}
              </div>
            )}
            </div>
          )}

          {/* Informações sobre a lista */}
          <div className="clientes-novos-section info-section">
            <h4>
              <AppIcon name="info" />
              Sobre esta lista
            </h4>
            <p className="info-text">
              A lista <strong>"Clientes Novos"</strong> recebe contatos que ainda não possuem cadastro na empresa.
              Configure as instruções acima para personalizar como o agente deve conduzir o primeiro atendimento.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="clientes-novos-modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Cancelar
          </button>
          <button 
            className="btn-save" 
            onClick={handleSave}
            disabled={loading}
          >
            <AppIcon name="check" />
            {loading ? 'Salvando...' : 'Salvar Configuração'}
          </button>
        </div>
      </div>
    </div>
  );
}
